import React from "react";
import { Link } from "react-router-dom";
import { Shield, GraduationCap, School, Rocket } from "lucide-react";
import { WavyDivider } from "../components/WavyDivider";
import { CTAStrip } from "../components/CTAStrip";

const HERO = "/images/IMG_7562.JPG";
const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/a6c16944-cdbb-43fa-8ac4-8a5b05ddef75/images/a78c61bc5fcba90fb869a6aea00c8ee7d2bd188a04c951099439b17290ba9af6.png";

const programs = [
  { name: "Playgroup", tag: "1-2 yrs", desc: "Fun activities for little ones", bg: "bg-[#DCE7FF]", icon: "🧱" },
  { name: "Nursery", tag: "2-3 yrs", desc: "Learning through play", bg: "bg-[#FFE1E1]", icon: "🎨" },
  { name: "Kindergarten", tag: "3-5 yrs", desc: "Building strong foundations", bg: "bg-[#FFF0DB]", icon: "📚" },
  { name: "Daycare", tag: "1-5 yrs", desc: "Safe and caring environment", bg: "bg-[#E6F8E4]", icon: "🧸" },
];

const whyUs = [
  { icon: Shield, label: "Safe Environment", tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
  { icon: GraduationCap, label: "Experienced Teachers", tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
  { icon: School, label: "Modern Facilities", tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
  { icon: Rocket, label: "Fun Activities", tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
];

const testimonials = [
  {
    quote: "My daughter has blossomed so much since joining Spring Kids. The teachers are warm, patient and truly love what they do.",
    name: "Priya R.",
    child: "Parent of a Nursery student",
    color: "bg-[#DCE7FF]",
    emoji: "🌸",
  },
  {
    quote: "The environment here is so safe and nurturing. My son looks forward to school every single morning — that says it all!",
    name: "Arjun M.",
    child: "Parent of a Playgroup student",
    color: "bg-[#FFF0DB]",
    emoji: "☀️",
  },
  {
    quote: "From art to yoga, the variety of activities keeps our child engaged and happy. Best decision we made for her early years.",
    name: "Sneha K.",
    child: "Parent of a Kindergarten student",
    color: "bg-[#E6F8E4]",
    emoji: "🌟",
  },
];

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-10 pb-4 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-8 items-center">
          <div className="animate-fade-up">
            <h1 className="text-5xl md:text-6xl font-bold text-kid-navy leading-tight">
              A Better Future <br /> Starts Here
            </h1>
            <p className="mt-5 text-lg text-kid-navy/70 max-w-md">
              Nurturing young minds with care, creativity, and confidence.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/enquiry" className="btn-lilac" data-testid="hero-enroll-btn">Enquire Now</Link>
              <Link to="/programs" className="btn-outline-navy" data-testid="hero-explore-btn">Explore Programs</Link>
            </div>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none">
              <div className="w-[90%] h-40 bg-[#DDE3FA] rounded-[50%] blur-sm" />
            </div>
            <img src={HERO} alt="Happy kids illustration" className="relative max-h-[440px] w-full object-cover rounded-3xl shadow-xl animate-float" data-testid="hero-image" />
          </div>
        </div>
        <WavyDivider fromColor="#EEF0FB" toColor="#FFFFFF" className="mt-6" />
      </section>

      {/* ABOUT strip */}
      <section className="bg-white pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-kid-navy mb-10">About Our School</h2>
          <div className="rounded-3xl bg-[#F3F4FC] overflow-hidden grid md:grid-cols-2 shadow-softer">
            <img src={ABOUT_IMG} alt="Teacher with children" className="w-full h-full object-cover min-h-[260px]" />
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <h3 className="text-2xl md:text-3xl font-bold text-kid-navy">Learn. Play. Grow.</h3>
              <p className="mt-3 text-kid-navy/75 text-base md:text-lg">
                We provide a nurturing environment where children can thrive and develop essential skills for a bright future.
              </p>
              <Link to="/about" className="btn-amber mt-6 self-start" data-testid="home-about-learn-more">Learn More</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-white pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="section-label">Our Programs</span>
          <h2 className="sr-only">Our Programs</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((p) => (
              <div key={p.name} className="kid-card p-0 overflow-hidden text-left" data-testid={`program-card-${p.name.toLowerCase()}`}>
                <div className={`h-36 ${p.bg} flex items-center justify-center text-6xl`}>{p.icon}</div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-kid-navy">{p.name}</h3>
                  <p className="text-kid-navy/70 mt-2 text-sm">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/programs" className="btn-outline-navy" data-testid="home-view-all-programs">View All Programs →</Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="section-label">Why Choose Us?</span>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((w) => (
              <div key={w.label} className="flex flex-col items-center gap-3" data-testid={`why-item-${w.label.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className={`w-24 h-24 rounded-full ${w.tint} flex items-center justify-center animate-bounce-soft`}>
                  <w.icon size={40} strokeWidth={2.2} className={w.color} />
                </div>
                <p className="font-semibold text-kid-navy">{w.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-kid-amber py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-3 gap-6 text-center text-white">
          {[
            { value: "Since 2010", label: "Established" },
            { value: "1200+", label: "Happy Kids" },
            { value: "30+", label: "Qualified Teachers" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl md:text-5xl font-bold">{s.value}</p>
              <p className="mt-1 text-sm md:text-base font-semibold opacity-80">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#F3F4FC] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="section-label">What Parents Say</span>
          <h2 className="text-3xl md:text-4xl font-bold text-kid-navy mt-2 mb-10">Loved by Families</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="kid-card text-left flex flex-col gap-4" data-testid={`testimonial-${t.name.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className={`w-12 h-12 rounded-full ${t.color} flex items-center justify-center text-2xl`}>{t.emoji}</div>
                <p className="text-kid-navy/80 text-base leading-relaxed flex-1">"{t.quote}"</p>
                <div>
                  <p className="font-bold text-kid-navy">{t.name}</p>
                  <p className="text-xs text-kid-navy/50">{t.child}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip topColor="#F3F4FC" />
    </div>
  );
}
